import { LiveMap, LiveObject } from "@liveblocks/client";
import { useRoom, useStorage } from "@liveblocks/react";
import { useAtom } from "jotai";
import { useCallback } from "react";
import { currentUserAtom } from "../jotai/atoms";
import { AnyBlock } from "../types";
import { nextUpdatedAt } from "../utils/updatedAt";
import { createBlockObj } from "./maps";

type UpdateArg = Partial<AnyBlock> & Pick<AnyBlock, "id">;

/**
 * useBlockAction returns block related CRUD function
 *
 * NOTE:
 * - the hook was implemented specifically with the goal of avoiding
 *   minimizing re-rendering. Hence we access the data with low lever
 *   Liveblock API (i.e. useRoom, storage)
 */
export function useBlockAction() {
  const [storage] = useStorage<{
    blocks: LiveMap<string, LiveObject<AnyBlock>>;
  }>();
  const room = useRoom();
  const [currentUser] = useAtom(currentUserAtom);

  const updateBlock = useCallback(
    (update: UpdateArg | UpdateArg[], raise = false) => {
      if (!storage) {
        throw new Error("internal error: storage not initialized yet");
      }
      room.batch(() => {
        const blocks = storage.get("blocks");
        const updates = Array.isArray(update) ? update : [update];

        for (let update of updates) {
          const client = blocks.get(update.id);
          if (!client) continue;

          if (raise) {
            update = { ...update, updatedAt: nextUpdatedAt() };
          }
          client.update(update);
        }
      });
    },
    [storage, room]
  );

  const deleteBlock = useCallback(
    (...ids: AnyBlock["id"][]) => {
      const updates = ids.map((id) => {
        return { id, deleted: true };
      });

      updateBlock(updates);
    },
    [updateBlock]
  );

  const createBlock = useCallback(
    (...blocks: Omit<AnyBlock, "id">[]) => {
      if (!storage) {
        throw new Error("internal error: storage not initialized yet");
      }

      const maps = storage.get("blocks");
      const items = blocks.map((x) => createBlockObj(x, currentUser?.id));

      room.batch(() => {
        for (const x of items) {
          maps.set(x.id, new LiveObject(x));
        }
      });
    },
    [room, storage, currentUser]
  );

  const duplicateBlock = useCallback(
    (...blocks: AnyBlock[]) => {
      const newBlocks = blocks.map((b) => {
        return {
          ...b,
          position: {
            x: b.position.x + 1,
            y: b.position.y + 1,
          },
        };
      });

      createBlock(...newBlocks);
    },
    [createBlock]
  );

  const cleanAllBlocks = useCallback(() => {
    if (!storage) {
      throw new Error("internal error: storage not initialized yet");
    }

    room.batch(() => {
      const map = storage.get("blocks");
      const ids = [...map.keys()];
      ids.forEach((id) => map.delete(id));
    });
  }, [room, storage]);

  return {
    updateBlock,
    deleteBlock,
    createBlock,
    duplicateBlock,
    cleanAllBlocks,
  };
}
